/**
 * A KPI citation → the `doppler://` URI the existing viewers already speak.
 *
 * There is no new previewer here and there must not be: chat citations already
 * open workbooks in `SpreadsheetViewer` with the cited cell highlighted, and
 * PDFs at their cited page through `openDopplerUri`. A KPI value's citation is
 * the same kind of claim about the same kind of file, so it goes through the
 * same door — this module only translates the shape.
 *
 * The translation needs the file's IDENTITY, which is contract v1.5's
 * `source.attachment_id`. A filename cannot be resolved to bytes (names repeat
 * across months and properties), so a citation without an id is a citation that
 * can only be read, not opened, and the caller renders it as text.
 */

import type { KpiSourceRef } from "../../types/kpi";

export type KpiSourceKind = "excel" | "pdf" | "unopenable";

/**
 * The number a citation is FOR — what the viewer looks for on a PDF page.
 *
 * A workbook citation names its cell, so the viewer can highlight it. A PDF
 * citation names a page and the column it was read from, and nothing narrower:
 * the only way to point at the figure is to find it. `value` is the EXTRACTED
 * number, not an override — an override is by definition not what the document
 * says.
 */
export interface KpiCitedNumber {
	value: number | null | undefined;
	unit?: string | null;
}

/** The column header out of a PDF ref: "p10:01/31/25" → "01/31/25". */
function pdfColumn(ref: string | null | undefined): string | null {
	const match = /^p\d+:(.+)$/.exec(ref?.trim() ?? "");
	return match ? match[1].trim() || null : null;
}

const EXCEL_SUFFIXES = [".xlsx", ".xlsm"];
const PDF_SUFFIXES = [".pdf"];

function suffixOf(file: string): string {
	const clean = file.split("?")[0].split("#")[0];
	const dot = clean.lastIndexOf(".");
	return dot === -1 ? "" : clean.slice(dot).toLowerCase();
}

/**
 * What kind of viewer this citation needs.
 *
 * Decided from the filename because that is what the fact table stores; the
 * extraction pipeline only ever reads .xlsx/.xlsm/.pdf, so anything else is a
 * row we cannot open and must not pretend to.
 */
export function kpiSourceKind(source: KpiSourceRef | null | undefined): KpiSourceKind {
	if (!source || !source.file) return "unopenable";
	if (
		source.attachment_id === null ||
		source.attachment_id === undefined ||
		!Number.isInteger(source.attachment_id) ||
		source.attachment_id <= 0
	) {
		return "unopenable";
	}
	const suffix = suffixOf(source.file);
	if (EXCEL_SUFFIXES.includes(suffix)) return "excel";
	if (PDF_SUFFIXES.includes(suffix)) return "pdf";
	return "unopenable";
}

/**
 * The `doppler://` href for a citation, or null when it cannot be opened.
 *
 * Excel refs carry the sheet plus either `cell` or `range` — the same
 * distinction `CitedExcelCell` makes, because `SpreadsheetViewer` highlights a
 * rect and needs to know which it was given. PDFs carry the page, and — when
 * the caller knows the number being cited — the figure to find on it, with the
 * column the ref names.
 */
export function kpiSourceHref(
	source: KpiSourceRef | null | undefined,
	cited?: KpiCitedNumber | null,
): string | null {
	const kind = kpiSourceKind(source);
	if (kind === "unopenable" || !source) return null;
	const params = new URLSearchParams();
	// The viewer titles itself from `file`; without it the header would read as
	// a generic "workbook" for a document the user just chose by name.
	params.set("file", source.file);

	if (kind === "excel") {
		if (source.sheet) params.set("sheet", source.sheet);
		const ref = source.ref?.trim();
		if (ref) {
			if (ref.includes(":")) params.set("range", ref);
			else params.set("cell", ref);
		}
		return `doppler://excel/${source.attachment_id}?${params.toString()}`;
	}

	if (source.page && source.page > 0) params.set("page", String(source.page));
	if (typeof cited?.value === "number" && Number.isFinite(cited.value)) {
		params.set("num", String(cited.value));
		if (cited.unit) params.set("unit", cited.unit);
		const column = pdfColumn(source.ref);
		if (column) params.set("col", column);
	}
	return `doppler://pdf/${source.attachment_id}?${params.toString()}`;
}

/** Human-readable location, used as the link text and the fallback text. */
export function kpiSourceLabel(source: KpiSourceRef): string {
	const parts = [source.file];
	if (source.sheet) parts.push(source.sheet);
	if (source.ref) parts.push(source.ref);
	if (source.page) parts.push(`p.${source.page}`);
	return parts.join(" · ");
}
